import { useEffect, useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { DialogContent_ } from "./DialogContent";
import { DialogSlideAsideChip_ } from "./DialogSlideAsideChip";
import { InsideDialogContext } from "./InsideDialogContext";
import { useDialogStackPosition } from "../DialogHostMutationContext";
import { DialogDirtyContext } from "../FormDirtyContext";
import { useDialogScrollLock } from "./useDialogScrollLock";
import { useDialogStackRegistration } from "./useDialogStackRegistration";
import { useDefaultDialogFooter } from "./useDefaultDialogFooter";
import { headingFontStyle } from "../../Theme/headingFontStyle";
import { DialogResult } from "../DialogResult";
import {
  useDialogTrayEntry,
  useDialogTrayGroupHoverOptional,
  useDialogTrayMutatorsOptional,
} from "../DialogTrayProvider";
import { DialogDepthProvider } from "./DialogDepthContext";
import {
  DIALOG_CONTENT_BASE_Z,
  DIALOG_OVERLAY_BASE_Z,
  DIALOG_STACK_STRIDE,
  dialogWidthValues,
  type DialogInnerProps,
} from "./dialogConstants";

export type { DialogInnerProps };

/**
 * The actual modal-mode dialog rendering. Rendered directly in the parent's
 * tree by DialogModal_, so state updates flow in a single render cycle and
 * controlled inputs reconcile naturally. When the parent unmounts Dialog
 * with open=true, DialogModal_'s cleanup pushes a closed snapshot of this
 * component into a DialogHost slot so the exit animation still completes
 * on a tree that survives the parent unmount.
 */
// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function DialogInner_({
  open = false,
  onOpenChange,
  children,
  footer,
  width = "default",
  title,
  description,
  warning,
  headerFilters,
  headerActions,
  menu,
  filterBar,
  fullHeight,
  hideCloseButton,
  onOk,
  onCancel,
  okLoading = false,
  okText = "OK",
  cancelText = "Cancel",
  confirmOnOk,
  okDisabledIfPristine = false,
  okDisabled = false,
  okDisabledMessage,
  draftPersistedExternally = false,
  stackId,
}: DialogInnerProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [peeked, setPeeked] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  // useState (not useRef) so the OK button re-renders the moment the user
  // touches a field. setIsDirty(true) is idempotent — React skips renders
  // once we're already dirty.
  const [isDirty, setIsDirty] = useState(false);

  // Only track dirty state when the dialog has save functionality (onOk).
  // Read-only dialogs should never prompt "Discard changes?" on close.
  const markDirty = useCallback(() => {
    if (onOk) setIsDirty(true);
  }, [onOk]);

  // Tray context: present iff this dialog is being rendered by
  // DialogTrayHost — i.e. it was opened via `tray.open(...)` or via
  // `<Button dialog={...} tray>` (which calls `tray.openElement` under
  // the hood). When present, the peek button parks via the tray instead
  // of using the local slide-aside; the chip displays the entry's parked
  // state and stacks vertically with peers.
  const trayEntry = useDialogTrayEntry();
  const tray = useDialogTrayMutatorsOptional();
  const groupHover = useDialogTrayGroupHoverOptional();
  const parked = trayEntry?.parked ?? false;
  const offscreen = peeked || parked;

  // Auto-name: push the dialog's `title` prop into the tray entry's
  // displayName so the chip shows "Edit shipment FS123" instead of the
  // generic registration default. Idempotent setName makes the per-render
  // call cheap.
  useEffect(() => {
    if (!trayEntry || !tray) return;
    // ReactNode titles (JSX) silently fall back to whatever
    // `tray.open(..., { name })` set originally — we'd need a separate
    // `chipName?: string` prop to support those without rendering JSX
    // into a chip. Add when a real call site needs it.
    if (typeof title !== "string") return;
    tray.setName(trayEntry.key, title);
  }, [trayEntry, tray, title]);

  // Auto-dirty: mirror local form-dirty state into the tray entry so the
  // chip's dirty dot and the Clear-all confirm's "N have unsaved changes"
  // count work without any per-call-site wiring. Idempotent setDirty
  // bails when nothing actually changed.
  useEffect(() => {
    if (!trayEntry || !tray) return;
    tray.setDirty(trayEntry.key, isDirty);
  }, [trayEntry, tray, isDirty]);

  useDialogScrollLock(stackId, open && !offscreen);
  useDialogStackRegistration(stackId, open);
  const stackPosition = useDialogStackPosition(stackId);

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const handleCancel = useCallback(() => {
    if (isDirty && !draftPersistedExternally) {
      setDiscardOpen(true);
      return;
    }
    onCancel?.();
    close();
  }, [onCancel, close, isDirty, draftPersistedExternally]);

  const executeOnOk = useCallback(async () => {
    if (!onOk) return;
    const result = await onOk();
    if (result === DialogResult.KeepOpen) return;
    setIsDirty(false);
    setConfirmOpen(false);
    close();
  }, [onOk, close]);

  const handleOk = useCallback(async () => {
    if (!onOk) return;
    if (confirmOnOk) {
      setConfirmOpen(true);
    } else {
      await executeOnOk();
    }
  }, [onOk, confirmOnOk, executeOnOk]);

  const handleDiscard = useCallback(() => {
    setDiscardOpen(false);
    setIsDirty(false);
    setPeeked(false);
    close();
  }, [close]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (okLoading) return;
        if (isDirty && !draftPersistedExternally) {
          setDiscardOpen(true);
          return;
        }
        setPeeked(false);
        close();
      }
    },
    [close, okLoading, isDirty, draftPersistedExternally],
  );

  const finalFooter = useDefaultDialogFooter({
    footer,
    onOk,
    onCancel,
    okLoading,
    okText,
    cancelText,
    onOkClick: handleOk,
    onCancelClick: handleCancel,
    okDisabled: okDisabled || (okDisabledIfPristine && !isDirty),
    ...(okDisabledMessage !== undefined && { okDisabledMessage }),
  });

  const overlayZ = DIALOG_OVERLAY_BASE_Z + stackPosition * DIALOG_STACK_STRIDE;
  const contentZ = DIALOG_CONTENT_BASE_Z + stackPosition * DIALOG_STACK_STRIDE;

  // When there's a visible title, wrap the h2 with Radix's Dialog.Title via
  // asChild so the accessible name IS the visible heading — no sr-only
  // duplicate. titleNode goes into DialogContent_'s header layout.
  const titleNode = title ? (
    <DialogPrimitive.Title asChild>
      <h2
        className="text-2xl font-bold text-foreground"
        style={headingFontStyle()}
      >
        {title}
      </h2>
    </DialogPrimitive.Title>
  ) : undefined;

  // Same asChild treatment as the title: the visible line IS the accessible
  // description, so a screen reader reads it once rather than twice.
  const descriptionNode = description ? (
    <DialogPrimitive.Description asChild>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </DialogPrimitive.Description>
  ) : undefined;

  // Heights are in `dvh`, never `vh`. Safari on iPad resolves `vh` against the
  // viewport with the browser toolbar hidden, so a `vh`-sized dialog extends
  // below what you can actually see and the footer's Save/Cancel buttons end up
  // unreachable. `dvh` tracks the height that's visible right now.
  //
  // WARNING: do not replace `-translate-x-1/2` with `transform: translateX(-50%)`.
  // The keyframes in index.css animate `transform`; they assume `translate` (a
  // separate CSS property in Tailwind v4) owns the centering. Combining both
  // on `transform` cancels the entry/exit animation and causes the dialog to
  // jump.
  const translateClass = offscreen
    ? "translate-x-[calc(50vw+2rem)]"
    : "-translate-x-1/2";

  // Peek button routes to park when this dialog is tray-managed. Page-mounted
  // dialogs (no trayEntry) keep the legacy slide-aside peek — useful for
  // brief "look up something behind me" flows that don't need cross-screen
  // survival.
  const handlePeek =
    trayEntry && tray ? () => tray.park(trayEntry.key) : () => setPeeked(true);

  const handleChipRestore = useCallback(() => {
    if (trayEntry && tray && parked) {
      tray.restore(trayEntry.key);
    } else {
      setPeeked(false);
    }
  }, [trayEntry, tray, parked]);

  // Parked chip top: the host computes an absolute top for every parked
  // chip via `chipTopCss` (it has full row-plan + hover state). Fall
  // back to the legacy `parkedIndex * 3.5rem` formula only as a safety
  // net for stories or hosts that haven't been migrated. Peek (legacy)
  // is always at 3vh — only one peek can be active per dialog, no
  // stacking needed.
  const chipTop = parked
    ? (trayEntry?.chipTopCss ??
      `calc(3vh + ${(trayEntry?.parkedIndex ?? 0) * 3.5}rem)`)
    : "3vh";
  // Parked chip normally sits behind any modal stack (z=30) so a freshly
  // opened dialog covers it. Two exceptions ride the dialog's own contentZ
  // (which is above anything below it in the stack): legacy peek (peek and
  // modal are mutually exclusive for the same dialog), and the opt-in
  // `chipAboveModals` entries — these were opened from within another
  // dialog and want their chip reachable on top of that parent's overlay
  // while it stays open.
  const chipAboveModals = trayEntry?.chipAboveModals ?? false;
  const chipZ = parked && !chipAboveModals ? 30 : contentZ;
  const chipLabel = title ?? "Resume";
  const titleAttr = typeof title === "string" ? title : undefined;

  return (
    <>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        modal={false}
      >
        <DialogPrimitive.Portal>
          {/* Manual overlay — DialogPrimitive.Overlay is not rendered when modal={false}.
              When peeked, the overlay stays in front but invisible to act as
              a "click anywhere to restore" surface. When parked, we want full
              page interactivity (the chip is the only restore affordance) so
              the overlay goes click-through.

              Blur and dim rather than a black wash: `blur(6px) brightness(90%)`
              is what Cargo used, and it leaves the page legible behind the
              dialog instead of flattening it to grey. */}
          <div
            data-state={open ? "open" : "closed"}
            className={`fixed inset-0 backdrop-blur-[6px] backdrop-brightness-90 transition-opacity duration-500 ease-in-out data-[state=closed]:animate-dialog-overlay-out data-[state=open]:animate-dialog-overlay-in ${peeked ? "cursor-pointer opacity-0" : ""} ${parked ? "pointer-events-none opacity-0" : ""}`}
            style={{ zIndex: overlayZ }}
            onClick={peeked ? () => setPeeked(false) : undefined}
            onKeyDown={
              peeked
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") setPeeked(false);
                  }
                : undefined
            }
            role={peeked ? "button" : undefined}
            tabIndex={peeked ? 0 : undefined}
            onWheel={
              peeked
                ? (e) => window.scrollBy({ top: e.deltaY, left: e.deltaX })
                : undefined
            }
          />
          <DialogPrimitive.Content
            // CRITICAL: when this dialog is parked, mark the Content as
            // `inert` so the browser excludes it from the focus tree, the
            // accessibility tree, and pointer-event hit-testing — even
            // though it stays mounted (preserving form state). Without
            // this, multiple parked-but-mounted Radix Dialog.Roots compete
            // for focus management with the active modal, which manifests
            // as "I clicked the active dialog's input but typing does
            // nothing" because focus is silently held by an off-screen
            // parked Content. `pointer-events-none` is a CSS belt for
            // browsers that haven't shipped inert yet.
            inert={parked}
            // Radix warns when Content has no Description; explicitly passing
            // aria-describedby={undefined} is its documented opt-out for
            // dialogs whose body speaks for itself. Only when there is no
            // description — otherwise Radix's auto-wiring must stay intact.
            {...(description === undefined && {
              "aria-describedby": undefined,
            })}
            className={`fixed top-[3dvh] left-1/2 flex max-h-[94dvh] flex-col transition-[translate,max-width] duration-500 ease-out ${translateClass} ${fullHeight ? "h-[94dvh]" : ""} ${width === "auto" ? "w-fit" : "w-full"} rounded-2xl border border-border bg-card shadow-2xl focus:outline-none data-[state=closed]:animate-dialog-content-out data-[state=open]:animate-dialog-content-in ${parked ? "pointer-events-none" : ""}`}
            style={{
              zIndex: contentZ,
              maxWidth: dialogWidthValues[width],
            }}
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
            onOpenAutoFocus={(e) => {
              // Prevent Radix's default auto-focus from landing on the
              // close/peek buttons (now keyboard-reachable). Users still
              // get them via Tab.
              e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (okLoading) e.preventDefault();
              if (peeked) {
                e.preventDefault();
                setPeeked(false);
              }
            }}
          >
            {/*
              Radix requires Dialog.Title inside Content for a11y. When we
              have a visible title, titleNode (rendered inside DialogContent_)
              satisfies that. When we don't, render an sr-only Title here so
              Radix is happy without forcing DialogContent_'s `hasHeader`
              layout to flip on for an invisible element.
            */}
            {!title && (
              <DialogPrimitive.Title className="sr-only">
                Dialog
              </DialogPrimitive.Title>
            )}
            <DialogDepthProvider value={stackPosition}>
              <InsideDialogContext.Provider value={true}>
                <DialogDirtyContext.Provider value={markDirty}>
                  <DialogContent_
                    titleNode={titleNode}
                    descriptionNode={descriptionNode}
                    warning={warning}
                    headerFilters={headerFilters}
                    headerActions={headerActions}
                    menu={menu}
                    filterBar={filterBar}
                    finalFooter={finalFooter}
                    onPeek={hideCloseButton ? undefined : handlePeek}
                    onClose={
                      hideCloseButton
                        ? undefined
                        : () => handleOpenChange(false)
                    }
                    okLoading={okLoading}
                  >
                    {children}
                  </DialogContent_>
                </DialogDirtyContext.Provider>
              </InsideDialogContext.Provider>
            </DialogDepthProvider>
          </DialogPrimitive.Content>
          {offscreen && (
            <DialogSlideAsideChip_
              parked={parked}
              isDirty={isDirty}
              closing={trayEntry?.closing ?? false}
              closingDelay={trayEntry?.closingDelay ?? 0}
              topCss={chipTop}
              zIndex={chipZ}
              titleAttr={titleAttr}
              label={chipLabel}
              onRestore={handleChipRestore}
              disabledForGrouping={
                parked && trayEntry !== undefined && !trayEntry.isOnOrigin
              }
              groupExpanded={trayEntry?.groupExpanded ?? false}
              onMouseEnter={
                parked && trayEntry && !trayEntry.isOnOrigin && groupHover
                  ? () => groupHover.hoverEnter(trayEntry.groupKey)
                  : undefined
              }
              onMouseLeave={
                parked && trayEntry && !trayEntry.isOnOrigin && groupHover
                  ? () => groupHover.hoverLeave()
                  : undefined
              }
            />
          )}
        </DialogPrimitive.Portal>
        {confirmOnOk && confirmOpen && (
          <ConfirmDialog
            open
            onClose={() => setConfirmOpen(false)}
            onConfirm={executeOnOk}
            title={confirmOnOk.title}
            message={confirmOnOk.message}
            confirmLabel={confirmOnOk.confirmLabel ?? "Confirm"}
            loading={okLoading}
          />
        )}
      </DialogPrimitive.Root>
      {/*
        Conditional render: keeping ConfirmDialog mounted while closed
        leaves a Radix Portal + provider tree per Dialog instance —
        adds up across nested dialogs.
      */}
      {discardOpen && (
        <ConfirmDialog
          open
          onClose={() => setDiscardOpen(false)}
          onConfirm={handleDiscard}
          title="Discard changes?"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmLabel="Discard"
          confirmTone="danger"
        />
      )}
    </>
  );
}
